import { Injectable, computed, signal } from '@angular/core';

export interface EvSocPrognosisWarning {
    startTime: Date | undefined;
    depotTime: Date;
    stateOfChargeAtDepot: number;
}

export interface EvSocPrognosisVehicleSnapshot {
    inventoryNumber: string;
    warnings: EvSocPrognosisWarning[];
}

export interface EvSocPrognosisGarageSnapshot {
    garageId: number;
    vehicles: EvSocPrognosisVehicleSnapshot[];
}

export interface EvSocPrognosisRow {
    rowId: string;
    garageId: number;
    inventoryNumber: string;
    warning: EvSocPrognosisWarning;
}

/** Keyed by garageId - holds the latest prognosis snapshot per garage. */
export type EvSocPrognosisMap = Map<number, EvSocPrognosisGarageSnapshot>;

@Injectable({ providedIn: 'root' })
export class EvSocPrognosisStore {
    private readonly _state = signal<EvSocPrognosisMap>(new Map());

    public readonly state = computed(() => this._state());
    public readonly rows = computed(() => this._flattenRows(this._state()));

    /** Replace the snapshot for one garage while keeping the rest intact. */
    public setSnapshot(snapshot: EvSocPrognosisGarageSnapshot): void {
        const next = new Map(this._state());
        next.set(snapshot.garageId, snapshot);
        this._state.set(next);
    }

    public remove(garageId: number): void {
        const next = new Map(this._state());
        next.delete(garageId);
        this._state.set(next);
    }

    public getRowsForVehicle(inventoryNumber: string): EvSocPrognosisRow[] {
        return this.rows().filter(row => row.inventoryNumber === inventoryNumber);
    }

    private _flattenRows(state: EvSocPrognosisMap): EvSocPrognosisRow[] {
        const rows: EvSocPrognosisRow[] = [];
        const seenWarnings = new Set<string>();

        for (const [garageId, snapshot] of state.entries()) {
            for (const vehicle of snapshot.vehicles) {
                vehicle.warnings.forEach((warning, warningIndex) => {
                    const warningKey = [
                        vehicle.inventoryNumber,
                        warning.startTime?.getTime() ?? '?',
                        warning.depotTime.getTime(),
                        warning.stateOfChargeAtDepot,
                    ].join(':');

                    if (seenWarnings.has(warningKey)) {
                        return;
                    }

                    seenWarnings.add(warningKey);

                    rows.push({
                        rowId: [
                            garageId,
                            vehicle.inventoryNumber,
                            warningIndex,
                            warning.startTime?.getTime() ?? 'na',
                            warning.depotTime.getTime(),
                            warning.stateOfChargeAtDepot,
                        ].join(':'),
                        garageId,
                        inventoryNumber: vehicle.inventoryNumber,
                        warning: {
                            startTime: warning.startTime,
                            depotTime: warning.depotTime,
                            stateOfChargeAtDepot: warning.stateOfChargeAtDepot,
                        },
                    });
                });
            }
        }

        return rows;
    }
}
